import React, { useEffect, useRef } from 'react';
import { ArrowForwardOutlined, Box, useBreakpoint } from '@landr/maestro';
import { ArrowHeroCta } from 'components/Icons/ArrowHeroCta';
import { PrismicButton, getIsSolidButtonKind } from 'components/PrismicButton';
import { GridRow, GridCol } from 'components/UI/Grid';
import { PrismicHTML } from 'components/Prismic';
import { BaseHeroSliceProps } from './types';
import { componentContentAnimation } from './Animations';
import { ActionButtonsWrapper, ArrowWrapper, HeroContent, HeroWrapper, TextWrapper } from './styled';

export const BaseHeroSlice: React.FC<BaseHeroSliceProps> = ({
    title,
    description,
    alignment,
    ctaKind,
    ctaLabel,
    ctaLink,
    secondaryCtaKind,
    secondaryCtaLabel,
    secondaryCtaLink,
    showArrow,
    sliceContext,
    isHeroVideo,
    children,
}) => {
    const isMobile = useBreakpoint('md');

    const heroElement = useRef<HTMLDivElement>(null);
    const heroTitle = useRef<HTMLDivElement>(null);
    const heroDescription = useRef<HTMLDivElement>(null);
    const heroActionsWrapper = useRef<HTMLDivElement>(null);
    const heroArrowWrapper = useRef<any>(null);

    const hasBothLinks = !!secondaryCtaLink && !!ctaLink;
    const primaryDefaultIcon = !getIsSolidButtonKind(ctaKind) ? ArrowHeroCta : undefined;
    const secondaryDefaultIcon = !getIsSolidButtonKind(secondaryCtaKind) ? ArrowHeroCta : undefined;

    const smoothScroll = (top: number) =>
        window.scrollTo({
            top,
            behavior: 'smooth',
        });

    const scrollPastHero = () => {
        if (heroElement && heroElement.current) {
            // The navbar is a different height on mobile than it is for desktop
            const navbarHeight = window.innerWidth <= 1024 ? 64 : 80;
            smoothScroll(heroElement.current.offsetTop + heroElement.current.offsetHeight - navbarHeight);
        }
    };

    useEffect(() => {
        componentContentAnimation(heroTitle, heroDescription, heroActionsWrapper, heroArrowWrapper);
    }, []);

    return (
        <HeroWrapper alignment={alignment} isHeroVideo={isHeroVideo} ref={heroElement}>
            <HeroContent>
                <GridRow horizontalAlign={'h-start'} verticalAlign={'v-center'}>
                    <GridCol colSize={`12`} offsetSize={'0'}>
                        <TextWrapper
                            as={'span'}
                            paddingTop={'120px'}
                            alignment={alignment}
                            maxWidth={isMobile ? '96%' : '680px'}
                            ref={heroTitle}
                        >
                            <PrismicHTML content={title} />
                        </TextWrapper>
                        <TextWrapper
                            as={'span'}
                            paddingTop={'0px'}
                            alignment={alignment}
                            isDescription={true}
                            maxWidth={isMobile ? '96%' : '680px'}
                            ref={heroDescription}
                        >
                            <PrismicHTML content={description} />
                        </TextWrapper>
                    </GridCol>
                    <GridCol colSize={`12`} offsetSize={'0'}>
                        <ActionButtonsWrapper alignment={alignment} ref={heroActionsWrapper}>
                            <GridRow
                                horizontalAlign={'h-center'}
                                verticalAlign={'v-center'}
                                className="Row--marginNone"
                            >
                                <GridCol mobileColSize={'12'} offsetSize={'0'} className="Col--paddingNone">
                                    {ctaLink && (
                                        <PrismicButton
                                            ctaKind={ctaKind}
                                            link={ctaLink}
                                            sliceContext={sliceContext}
                                            label={ctaLabel}
                                            size="lg"
                                            defaultIcon={primaryDefaultIcon}
                                        />
                                    )}
                                </GridCol>
                                <GridCol mobileColSize={'12'} offsetSize={'0'} className="paddingNone">
                                    {secondaryCtaLink && (
                                        <Box
                                            paddingLeft={hasBothLinks && !isMobile ? '20px' : '0px'}
                                            paddingTop={isMobile ? '10px' : '0px'}
                                        >
                                            <PrismicButton
                                                ctaKind={secondaryCtaKind}
                                                link={secondaryCtaLink}
                                                sliceContext={sliceContext}
                                                label={secondaryCtaLabel}
                                                size="lg"
                                                defaultIcon={secondaryDefaultIcon}
                                            />
                                        </Box>
                                    )}
                                </GridCol>
                            </GridRow>
                            {showArrow === 'Yes' && (
                                <ArrowWrapper
                                    alignment={alignment}
                                    onClick={scrollPastHero}
                                    marginTop={!hasBothLinks ? '0px' : '40px'}
                                    data-testid="heroArrow"
                                    ref={heroArrowWrapper}
                                    kind="link"
                                    variant="secondary"
                                    width={40}
                                    height={40}
                                    padding={0}
                                >
                                    <ArrowForwardOutlined size="lg" />
                                </ArrowWrapper>
                            )}
                        </ActionButtonsWrapper>
                    </GridCol>
                </GridRow>
            </HeroContent>
            {children}
        </HeroWrapper>
    );
};
